import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Shield, FileCheck, AlertTriangle, Clock, Upload, Trash2, CheckCircle2, XCircle, Plus, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_CONFIG = {
  green: { label: 'Compliant', bg: 'bg-emerald-500', text: 'text-emerald-500', lightBg: 'bg-emerald-500/10', desc: 'All documents are up to date' },
  amber: { label: 'Expiring Soon', bg: 'bg-amber-500', text: 'text-amber-500', lightBg: 'bg-amber-500/10', desc: 'Some documents expire within 6 weeks' },
  red: { label: 'Expired', bg: 'bg-red-500', text: 'text-red-500', lightBg: 'bg-red-500/10', desc: 'One or more documents have expired' },
  black: { label: 'Outstanding', bg: 'bg-zinc-700', text: 'text-zinc-400', lightBg: 'bg-zinc-500/10', desc: 'Required documents are missing' },
};

const DOC_STATUS_CONFIG = {
  valid: { label: 'Valid', icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  expiring_soon: { label: 'Expiring Soon', icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  expired: { label: 'Expired', icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
  outstanding: { label: 'No Expiry Set', icon: AlertTriangle, color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
};

const DOC_TYPES = ['DBS Check', 'Right to Work', 'Training Certificate', 'Insurance', 'First Aid Certificate', 'Health Declaration', 'Qualifications', 'Driving License', 'Reference', 'Safeguarding Certificate'];

export default function MyCompliance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ document_type: 'DBS Check', name: '', expiry_date: '', notes: '' });

  useEffect(() => { fetchCompliance(); }, []);

  const fetchCompliance = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/caregiver-management/my-compliance`, { withCredentials: true });
      setData(d);
    } catch { toast.error('Failed to load compliance data'); }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Enter a document title'); return; }
    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/caregiver-management/documents`, form, { withCredentials: true });
      toast.success('Document uploaded');
      setShowUpload(false);
      setForm({ document_type: 'DBS Check', name: '', expiry_date: '', notes: '' });
      fetchCompliance();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/caregiver-management/documents/${docId}`, { withCredentials: true });
      toast.success('Document removed');
      fetchCompliance();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return (
    <DashboardLayout><div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div></DashboardLayout>
  );

  const overall = data?.overall_status || 'black';
  const config = COLOR_CONFIG[overall];
  const docs = data?.documents || [];
  const missing = data?.missing_types || [];
  const stats = data?.stats || {};

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="my-compliance-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">My Documents</h1>
            <p className="text-muted-foreground mt-1">Upload and track your compliance documents</p>
          </div>
          <Dialog open={showUpload} onOpenChange={setShowUpload}>
            <DialogTrigger asChild>
              <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" data-testid="upload-doc-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-card border-border sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-foreground">Upload Compliance Document</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <Label className="text-foreground">Document Type</Label>
                  <Select value={form.document_type} onValueChange={v => setForm(f => ({ ...f, document_type: v }))}>
                    <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="doc-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map(t => (
                        <SelectItem key={t} value={t}>
                          <span className="flex items-center gap-2">
                            {missing.includes(t) && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                            {t}
                            {missing.includes(t) && <span className="text-xs text-red-400 ml-1">Required</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-foreground">Title / Reference</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. DBS Certificate #12345" className="mt-1 bg-muted border-border text-foreground" data-testid="doc-title-input" />
                </div>
                <div>
                  <Label className="text-foreground">Expiry Date</Label>
                  <Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="doc-expiry-input" />
                  <p className="text-xs text-muted-foreground mt-1">Set the expiry date so you get alerts before it lapses</p>
                </div>
                <div>
                  <Label className="text-foreground">Notes (optional)</Label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full rounded-md bg-muted border border-border text-foreground p-3 text-sm min-h-[60px] resize-none" placeholder="Any additional notes..." data-testid="doc-notes-input" />
                </div>
                <Button type="submit" className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" disabled={uploading} data-testid="submit-doc-btn">
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Overall Status Banner */}
        <Card className={`border ${config.bg === 'bg-emerald-500' ? 'border-emerald-500/30' : config.bg === 'bg-amber-500' ? 'border-amber-500/30' : config.bg === 'bg-red-500' ? 'border-red-500/30' : 'border-zinc-500/30'}`} data-testid="compliance-banner">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`w-5 h-5 rounded-full ${config.bg}`} />
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${config.text}`}>{config.label}</h3>
                <p className="text-sm text-muted-foreground">{config.desc}</p>
              </div>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div><p className="text-xl font-bold text-emerald-500">{stats.valid || 0}</p><p className="text-[10px] text-muted-foreground">Valid</p></div>
                <div><p className="text-xl font-bold text-amber-500">{stats.expiring_soon || 0}</p><p className="text-[10px] text-muted-foreground">Expiring</p></div>
                <div><p className="text-xl font-bold text-red-500">{stats.expired || 0}</p><p className="text-[10px] text-muted-foreground">Expired</p></div>
                <div><p className="text-xl font-bold text-zinc-400">{stats.outstanding || 0}</p><p className="text-[10px] text-muted-foreground">No Expiry</p></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Missing Documents Alert */}
        {missing.length > 0 && (
          <Card className="border-red-500/20 bg-red-500/5" data-testid="missing-docs-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Required Documents Missing</p>
                  <p className="text-xs text-muted-foreground mt-1">Upload these to become fully compliant:</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {missing.map(type => (
                      <button
                        key={type}
                        onClick={() => { setForm(f => ({ ...f, document_type: type })); setShowUpload(true); }}
                        className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400 hover:bg-red-500/20 transition-colors cursor-pointer"
                        data-testid={`missing-${type.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        + {type}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document List */}
        <Card className="glass-card border-border" data-testid="documents-list">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-[#00A6A0]" />
              My Documents ({docs.length})
            </CardTitle>
            <CardDescription>Your uploaded compliance documents with expiry tracking</CardDescription>
          </CardHeader>
          <CardContent>
            {docs.length === 0 ? (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-muted-foreground">No documents uploaded yet</p>
                <p className="text-xs text-muted-foreground mt-1">Click "Upload Document" to get started</p>
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map(doc => {
                  const statusConf = DOC_STATUS_CONFIG[doc.compliance_status] || DOC_STATUS_CONFIG.outstanding;
                  const StatusIcon = statusConf.icon;
                  return (
                    <div key={doc.id} className="flex items-center gap-3 p-4 rounded-xl bg-muted/30 border border-border/50 group" data-testid={`doc-item-${doc.id}`}>
                      <div className={`p-2 rounded-lg ${statusConf.bg}`}>
                        <StatusIcon className={`w-4 h-4 ${statusConf.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">{doc.name || doc.title || 'Untitled'}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${statusConf.bg} ${statusConf.color}`}>{statusConf.label}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{doc.document_type}</span>
                          {doc.expiry_date && (
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-3 h-3" />
                              Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                            </span>
                          )}
                          <span>Uploaded: {new Date(doc.created_at).toLocaleDateString()}</span>
                        </div>
                        {doc.notes && <p className="text-xs text-muted-foreground mt-1 italic">{doc.notes}</p>}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDelete(doc.id)}
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
